import { useMemo } from 'react';
import { createFileRoute, Outlet, useChildMatches, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Users } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { DetailCard, DetailField, DetailFieldGrid } from '@/components/molecules';
import { useWorkspaceHeader } from '@/components/layouts';
import { formatDate } from '@/components/organisms/settings-groups/group-display';
import { useGroupDetail, useGroups } from '@/hooks/settings/useGroups';
import type { GroupDetail, GroupListItem } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/groups/$id')({
  component: GroupDetailRoute,
});

function formatMemberName(member: GroupDetail['members'][number]) {
  return (
    member.displayName ||
    [member.firstName, member.lastName].filter(Boolean).join(' ') ||
    member.email
  );
}

function GroupDetailRoute() {
  const { id } = Route.useParams();
  const router = useRouter();
  const childMatches = useChildMatches();
  const { data: groupsData } = useGroups();
  const groups = useMemo(() => groupsData?.data ?? [], [groupsData?.data]);

  const decodedId = decodeURIComponent(id);
  const listGroup = useMemo(
    () => groups.find((candidate) => candidate.id === decodedId) ?? null,
    [decodedId, groups],
  );
  const { data: group, isLoading: groupLoading } = useGroupDetail(decodedId);

  const displayGroup = group ?? listGroup;

  // Let the edit child route (/$id/edit) mount through the Outlet instead of
  // always rendering the read-only detail (mirrors permissions/$id).
  if (childMatches.length > 0) {
    return <Outlet />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <GroupDetailContextHeaderSlot group={displayGroup} />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <GroupDetailBody
          listGroup={listGroup}
          group={group ?? null}
          isLoadingGroup={Boolean(decodedId) && groupLoading}
          onBack={() => void router.navigate({ to: '/dashboard/settings/groups' })}
          onEdit={(groupId) =>
            void router.navigate({
              to: '/dashboard/settings/groups/$id/edit',
              params: { id: groupId },
            })
          }
        />
      </div>
    </div>
  );
}

function GroupDetailBody({
  listGroup,
  group,
  isLoadingGroup,
  onBack,
  onEdit,
}: {
  listGroup: GroupListItem | null;
  group: GroupDetail | null;
  isLoadingGroup: boolean;
  onBack: () => void;
  onEdit: (groupId: string) => void;
}) {
  const displayGroup = group ?? listGroup;
  const memberCount = listGroup?.memberCount ?? group?.members.length ?? 0;
  const managerName = group?.managerName ?? listGroup?.managerName ?? null;
  const isActive = group?.isActive ?? listGroup?.isActive ?? true;
  const createdAt = group?.createdAt ?? listGroup?.createdAt;
  const updatedAt = group?.updatedAt ?? listGroup?.updatedAt;

  if (!displayGroup) {
    return (
      <DetailCard label="Group" icon={Users}>
        <p className="text-sm text-muted-foreground">
          {isLoadingGroup ? 'Loading group...' : 'Group not found.'}
        </p>
      </DetailCard>
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <Button
              variant="ghost"
              size="icon-sm"
              className="-ml-1 shrink-0 md:hidden"
              onClick={onBack}
              aria-label="Back to groups"
            >
              <ArrowLeft size={16} />
            </Button>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Users className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-medium tracking-normal">
                  {displayGroup.name}
                </h1>
                <Badge variant={isActive ? 'default' : 'secondary'} className="shrink-0">
                  {isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                {displayGroup.description || 'No description.'}
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => onEdit(displayGroup.id)}
          >
            <Pencil className="size-4" />
            Edit
          </Button>
        </div>

        <DetailFieldGrid className="mt-4 border-t pt-4 sm:grid-cols-2">
          <DetailField label="Members" value={memberCount} />
          <DetailField
            label="Manager"
            value={managerName ?? 'No manager'}
            valueClassName="break-words"
          />
          <DetailField label="Created" value={formatDate(createdAt)} />
          <DetailField label="Updated" value={formatDate(updatedAt)} />
        </DetailFieldGrid>
      </section>

      <DetailCard label={`Members (${memberCount})`} icon={Users}>
        {isLoadingGroup && !group ? (
          <p className="text-sm text-muted-foreground">Loading members...</p>
        ) : !group || group.members.length === 0 ? (
          <p className="text-sm text-muted-foreground">No members assigned.</p>
        ) : (
          <div className="space-y-2">
            {group.members.map((member) => (
              <div key={member.membershipId} className="rounded-md border bg-muted/20 p-3">
                <p className="truncate text-sm font-medium">{formatMemberName(member)}</p>
                <p className="truncate text-xs text-muted-foreground">{member.email}</p>
              </div>
            ))}
          </div>
        )}
      </DetailCard>
    </div>
  );
}

function GroupDetailContextHeaderSlot({ group }: { group: GroupListItem | GroupDetail | null }) {
  const contextHeader = useMemo(() => {
    if (!group) return null;
    return (
      <div className="flex min-w-0 flex-col">
        <h2 className="truncate text-base font-semibold">{group.name}</h2>
        <p className="truncate text-xs text-muted-foreground">
          {group.description || 'View group details and members.'}
        </p>
      </div>
    );
  }, [group]);

  useWorkspaceHeader({ contextHeader }, [contextHeader]);
  return null;
}
